import { useLayoutEffect, useState, RefObject } from 'react';

type PositionLike = { top?: number; left?: number; width?: number };

function positionsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const pa = a as PositionLike;
  const pb = b as PositionLike;
  return pa.top === pb.top && pa.left === pb.left && pa.width === pb.width;
}

/**
 * Keeps a dropdown/popover position in sync with its trigger on scroll and resize,
 * so the menu stays visually attached (e.g. "More actions" / ContextMenu behavior).
 *
 * - When `open` becomes true, position is computed in useLayoutEffect (before paint).
 * - One rAF remasure after the menu mounts (real size → flip / viewport clamp).
 * - Skips setState when top/left/width are unchanged (avoids extra re-renders).
 * - On window scroll (except inside `menuRef`) and resize, position is recomputed.
 */
export function useFollowTriggerPosition<T>({
  open,
  triggerRef,
  menuRef,
  computePosition,
}: {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  menuRef?: RefObject<HTMLElement | null>;
  computePosition: (triggerRect: DOMRect, menu: HTMLElement | null) => T;
}): T | null {
  const [position, setPosition] = useState<T | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const next = computePosition(rect, menuRef?.current ?? null);
      setPosition((prev) => (positionsEqual(prev, next) ? prev : next));
    };

    updatePosition();

    // One remasure after menu paints with real dimensions
    const raf = requestAnimationFrame(updatePosition);

    const handleScroll = (e: Event) => {
      if (menuRef?.current?.contains(e.target as Node)) return;
      updatePosition();
    };

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, triggerRef, menuRef, computePosition]);

  return position;
}
